import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { useAuth } from "@/context/AuthContext";

// Loan Calculator Component
export const LoanCalculator = () => {
  const [principal, setPrincipal] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [termMonths, setTermMonths] = useState("");
  const [originationFee, setOriginationFee] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
        // Use pending new rate for new calculations, otherwise current vextir
        const prc = response.data.pending_rate_change;
        const totalRate = (prc && prc.status === 'pending') ? prc.new_rate : (response.data.vextir || 17.7);
        setInterestRate(totalRate.toString());
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // Calculate origination fee whenever principal or settings change
  useEffect(() => {
    if (principal && !isNaN(parseFloat(principal)) && settings) {
      const amount = parseFloat(principal);
      const threshold = 1000000; // 1,000,000 kr
      
      // Determine which percentage to use
      const percent = amount < threshold 
        ? (settings.lantokugjald_percent_under_1m / 100 || 0)
        : (settings.lantokugjald_percent_over_1m / 100 || 0);
      
      const percentFee = amount * percent;
      const minimumFee = settings.lantokugjald || 0;
      const fee = Math.max(percentFee, minimumFee);
      setOriginationFee(fee.toString());
    } else {
      setOriginationFee("");
    }
  }, [principal, settings]);

  const calculateLoan = async () => {
    if (!principal || !interestRate || !termMonths) {
      toast.error("Vinsamlegast fylltu í alla reiti");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/loans/calculate`, {
        principal: parseFloat(principal),
        interest_rate: parseFloat(interestRate),
        term_months: parseInt(termMonths),
        origination_fee: parseFloat(originationFee) || 0
      });
      setResult(response.data);
    } catch (error) {
      console.error("Error calculating loan:", error);
      toast.error("Ekki tókst að reikna lán");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="loan-calculator">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Lánareiknivél</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle>Lánaútreikning</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="principal">Upphæð (kr.)</Label>
                <Input
                  id="principal"
                  type="number"
                  placeholder="5.000.000"
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value)}
                  data-testid="principal-input"
                />
              </div>
              
              <div>
                <Label htmlFor="origination-fee">Lántökugjald (kr.)</Label>
                <Input
                  id="origination-fee"
                  type="text"
                  value={originationFee ? `${formatIcelandicNumber(Math.round(parseFloat(originationFee)))} kr.` : ""}
                  readOnly
                  className="bg-gray-100 text-gray-700"
                  placeholder="Reiknast sjálfkrafa"
                  data-testid="origination-fee-input"
                />
                {settings && principal && (
                  <p className="text-xs text-gray-500 mt-1">
                    {parseFloat(principal) < 1000000 
                      ? `${settings.lantokugjald_percent_under_1m}% fyrir lán undir 1M`
                      : `${settings.lantokugjald_percent_over_1m}% fyrir lán yfir 1M`
                    } (lágmark {formatIcelandicNumber(Math.round(settings.lantokugjald || 0))} kr.)
                  </p>
                )}
                {settings && !principal && (
                  <p className="text-xs text-gray-500 mt-1">
                    {settings.lantokugjald_percent_under_1m}% undir 1M / {settings.lantokugjald_percent_over_1m}% yfir 1M (lágmark {formatIcelandicNumber(Math.round(settings.lantokugjald || 0))} kr.)
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="interest-rate">Vextir (%)</Label>
                <Input
                  id="interest-rate"
                  type="text"
                  value={interestRate ? `${interestRate}%` : "Hleð..."}
                  readOnly
                  className="bg-gray-100 text-gray-700"
                  data-testid="interest-rate-input"
                />
                {settings && (
                  <p className="text-xs text-gray-500 mt-1">
                    Núverandi vextir: {settings.vextir}%
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="term">Lánstími (mánuðir)</Label>
                <Input
                  id="term"
                  type="number"
                  placeholder="60"
                  value={termMonths}
                  onChange={(e) => setTermMonths(e.target.value)}
                  data-testid="term-input"
                />
              </div>
              
              <Button 
                onClick={calculateLoan} 
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="calculate-btn"
              >
                {loading ? "Reikna..." : "Reikna"}
              </Button>
            </CardContent>
          </Card>

          {result && (
            <>
              {/* OLD vs NEW Comparison for Skuldbreyting */}
              {isSkuldbreyting && skuldbreytingSelectedLoans.length > 0 && (
                <Card className="bg-yellow-50 border-yellow-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      Samanburður: Eldra lán vs. Nýtt lán
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">ELDRA LÁN</p>
                        {skuldbreytingSelectedLoans.map(loan => (
                          <div key={loan.id} className="text-xs space-y-1 mb-3 pb-3 border-b">
                            <p className="font-semibold">Lán #{loanNumberMap[loan.id] || '?'}</p>
                            <div className="flex justify-between">
                              <span>Vextir:</span>
                              <span className="font-bold">{loan.interest_rate?.toFixed(2) || settings.vextir?.toFixed(2) || '17.7'}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Mán. greiðsla:</span>
                              <span className="font-bold">
                                {(() => {
                                  if (loan.payment_schedule && loan.payment_schedule.length > 0) {
                                    const avgPayment = loan.payment_schedule.reduce((sum, p) => sum + (p.payment || 0), 0) / loan.payment_schedule.length;
                                    return formatIcelandicNumber(Math.round(avgPayment));
                                  }
                                  return formatIcelandicNumber(Math.round(loan.monthly_payment || 0));
                                })()} kr.
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Eftirstöðvar:</span>
                              <span className="font-bold text-blue-700">
                                {formatIcelandicNumber(Math.round(loan.remaining_balance || loan.loan_amount))} kr.
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-green-600 mb-2">NÝTT LÁN (Núverandi vextir)</p>
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between">
                            <span>Vextir:</span>
                            <span className="font-bold text-green-700">{(() => { const p = settings.pending_rate_change; return (p && p.status === 'pending') ? p.new_rate.toFixed(2) : (settings.vextir?.toFixed(2) || '17.7'); })()}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Mán. greiðsla:</span>
                            <span className="font-bold text-green-700">
                              {formatIcelandicNumber(Math.round(result.monthly_payment))} kr.
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Lánstími:</span>
                            <span className="font-bold">{termMonths} mán.</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Heildargreiðsla:</span>
                            <span className="font-bold">
                              {formatIcelandicNumber(Math.round(result.total_payment))} kr.
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 p-2 bg-green-100 rounded">
                          <p className="text-xs text-green-800 font-semibold">
                            ℹ️ Nýtt lán notar núverandi vexti frá kerfisstillingum
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
            <Card>
            <CardHeader>
              <CardTitle>Niðurstöður útreikninga</CardTitle>
            </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span>Mánaðarleg greiðsla:</span>
                    <span className="font-bold text-black" data-testid="monthly-payment">
                      {formatIcelandicNumber(Math.round(result.monthly_payment))} kr.
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Heildargreiðsla:</span>
                    <span className="font-bold text-black" data-testid="total-payment">
                      {formatIcelandicNumber(Math.round(result.total_payment))} kr.
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Heildarvextir:</span>
                    <span className="font-bold text-black" data-testid="total-interest">
                      {formatIcelandicNumber(Math.round(result.total_interest))} kr.
                    </span>
                  </div>
                  {result.origination_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Lántökugjald:</span>
                      <span className="font-bold text-black" data-testid="origination-fee">
                        {formatIcelandicNumber(Math.round(result.origination_fee))} kr.
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Lánsfjárhæð:</span>
                    <span className="font-bold text-black" data-testid="loan-disbursement">
                      {formatIcelandicNumber(Math.round(result.loan_disbursement))} kr.
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Heildarkostnaður:</span>
                    <span className="font-bold text-black" data-testid="total-cost">
                      {formatIcelandicNumber(Math.round(result.total_cost))} kr.
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Árleg hlutfallstala:</span>
                    <span className={`font-bold ${(() => {
                      const totalCost = result.total_cost - result.loan_disbursement;
                      const termInYears = parseInt(termMonths) / 12;
                      const apr = termInYears > 0 ? (totalCost / result.loan_disbursement) * (1 / termInYears) * 100 : 0;
                      return apr > 35 ? 'text-red-600' : 'text-blue-600';
                    })()}`} data-testid="apr">
                      {(() => {
                        const totalCost = result.total_cost - result.loan_disbursement;
                        const termInYears = parseInt(termMonths) / 12;
                        const apr = termInYears > 0 ? (totalCost / result.loan_disbursement) * (1 / termInYears) * 100 : 0;
                        return apr.toFixed(2);
                      })()}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            </>
          )}

          {/* Payment Schedule */}
          {result && (
            <>
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Greiðsluáætlun</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-center">Mánuður</TableHead>
                        <TableHead className="text-right">Greiðsla</TableHead>
                        <TableHead className="text-right">Höfuðstóll</TableHead>
                        <TableHead className="text-right">Vextir</TableHead>
                        <TableHead className="text-right">Eftirstöðvar</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.payment_schedule.map((payment, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="text-center font-medium">
                            {payment.month}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatIcelandicNumber(Math.round(payment.payment))} kr.
                          </TableCell>
                          <TableCell className="text-right">
                            {formatIcelandicNumber(Math.round(payment.principal))} kr.
                          </TableCell>
                          <TableCell className="text-right">
                            {formatIcelandicNumber(Math.round(payment.interest))} kr.
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatIcelandicNumber(Math.round(payment.balance))} kr.
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

